import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import SuperadminLayout from './SuperadminLayout';
import axios from '../../axios/axiosconf';
import { ordenarListaAlfabetica } from './superadminSortUtils';
import './SuperadminStyles.css';

const ContraseñasUsuarios = () => {
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [audit, setAudit] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [clientFilter, setClientFilter] = useState('');
    const [alert, setAlert] = useState({ show: false, type: '', message: '' });
    const [showRevealModal, setShowRevealModal] = useState(false);
    const [revealing, setRevealing] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [revealedPassword, setRevealedPassword] = useState('');

    useEffect(() => {
        const token = localStorage.getItem('superadmin_token');
        const user = localStorage.getItem('superadmin_user');

        if (!token || !user) {
            history.push('/superadmin/login');
            return;
        }

        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        fetchAll();
    }, [history]);

    const fetchAll = async () => {
        const token = axios.defaults.headers.common['Authorization'];
        const headers = token ? { Authorization: token } : {};

        let listOk = false;
        try {
            const listRes = await axios.get('/superadmin/macros-passwords', { headers });
            setItems(Array.isArray(listRes.data) ? listRes.data : []);
            listOk = true;
        } catch (error) {
            if (error.response?.status === 401) {
                localStorage.removeItem('superadmin_token');
                localStorage.removeItem('superadmin_user');
                history.push('/superadmin/login');
                return;
            }
            setItems([]);
            setAlert({ show: true, type: 'danger', message: 'No se pudo cargar el listado de contraseñas.' });
        }

        try {
            const auditRes = await axios.get('/superadmin/macros-passwords/audit', { headers });
            setAudit(Array.isArray(auditRes.data) ? auditRes.data : []);
        } catch (error) {
            setAudit([]);
            if (listOk) {
                setAlert({
                    show: true,
                    type: 'warning',
                    message: 'Listado cargado, pero la auditoría no está disponible (tabla macros_password_audit). Ejecute la migración en el servidor.',
                });
            }
        }

        try {
            const clientesRes = await axios.get('/superadmin/clientes', { headers });
            setClientes(Array.isArray(clientesRes.data) ? clientesRes.data : clientesRes.data?.data || []);
        } catch {
            setClientes([]);
        }

        setLoading(false);
    };

    const filteredItems = useMemo(() => {
        let rows = items;
        if (clientFilter) {
            rows = rows.filter((r) => String(r.client_id) === String(clientFilter));
        }
        if (searchTerm.trim()) {
            const q = searchTerm.trim().toLowerCase();
            rows = rows.filter((r) =>
                `${r.name || ''} ${r.lastname || ''}`.toLowerCase().includes(q) ||
                (r.email || '').toLowerCase().includes(q) ||
                (r.cliente_nombre || '').toLowerCase().includes(q)
            );
        }
        return ordenarListaAlfabetica(
            rows.map((r) => ({ ...r, _orden: `${r.name || ''} ${r.lastname || ''}`.trim() })),
            '_orden'
        );
    }, [items, searchTerm, clientFilter]);

    const handleRevealClick = (row) => {
        setSelectedUser(row);
        setRevealedPassword('');
        setShowRevealModal(true);
    };

    const handleRevealConfirm = async () => {
        if (!selectedUser) return;
        setRevealing(true);
        try {
            const response = await axios.get(`/superadmin/macros-passwords/${selectedUser.user_id}/reveal`);
            setRevealedPassword(response.data.password || '');
            const auditRes = await axios.get('/superadmin/macros-passwords/audit');
            setAudit(Array.isArray(auditRes.data) ? auditRes.data : []);
        } catch (error) {
            setAlert({
                show: true,
                type: 'danger',
                message: error.response?.data?.message || 'No se pudo revelar la contraseña.',
            });
            setShowRevealModal(false);
        } finally {
            setRevealing(false);
        }
    };

    const tipoLabel = (tipo) => (String(tipo) === '1' ? 'Administrativo' : 'Médico');

    if (loading) {
        return (
            <SuperadminLayout title="Contraseñas de usuarios">
                <div className="text-center py-5">Cargando…</div>
            </SuperadminLayout>
        );
    }

    return (
        <SuperadminLayout title="Contraseñas de usuarios">
            {alert.show && (
                <div className={`alert alert-${alert.type} alert-dismissible`} role="alert">
                    {alert.message}
                    <button type="button" className="close" onClick={() => setAlert({ show: false, type: '', message: '' })}>
                        <span>&times;</span>
                    </button>
                </div>
            )}

            <div className="alert alert-warning">
                <i className="fas fa-shield-alt mr-2"></i>
                Acceso restringido. Cada consulta del listado y cada revelado quedan registrados en auditoría.
            </div>

            <div className="card mb-4">
                <div className="card-body">
                    <div className="row mb-3">
                        <div className="col-md-4">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Buscar por nombre o email…"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="col-md-4">
                            <select className="form-control" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
                                <option value="">Todos los clientes</option>
                                {clientes.map((c) => (
                                    <option key={c.id} value={c.id}>{c.nombre}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-4 text-md-right pt-2 pt-md-0">
                            <span className="badge badge-info p-2">{filteredItems.length} registros</span>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table className="table table-sm table-hover">
                            <thead className="thead-light">
                                <tr>
                                    <th>Usuario</th>
                                    <th>Email</th>
                                    <th>Cliente</th>
                                    <th>Tipo</th>
                                    <th>Estado</th>
                                    <th>Actualizada</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredItems.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="text-center text-muted py-4">No hay contraseñas almacenadas.</td>
                                    </tr>
                                ) : filteredItems.map((row) => (
                                    <tr key={row.user_id}>
                                        <td>{row.name} {row.lastname}</td>
                                        <td>{row.email}</td>
                                        <td>{row.cliente_nombre || `#${row.client_id}`}</td>
                                        <td>{tipoLabel(row.tipo_usuario)}</td>
                                        <td>
                                            {parseInt(row.activo, 10) === 1
                                                ? <span className="badge badge-success">Activo</span>
                                                : <span className="badge badge-secondary">Inactivo</span>}
                                        </td>
                                        <td>{row.password_updated_at ? new Date(row.password_updated_at).toLocaleString() : '—'}</td>
                                        <td className="text-right">
                                            <button
                                                type="button"
                                                className="btn btn-outline-danger btn-sm"
                                                onClick={() => handleRevealClick(row)}
                                            >
                                                <i className="fas fa-key mr-1"></i> Ver
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="card-header">
                    <strong>Auditoría reciente</strong>
                </div>
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-sm mb-0">
                            <thead className="thead-light">
                                <tr>
                                    <th>Fecha</th>
                                    <th>Superadmin</th>
                                    <th>Acción</th>
                                    <th>Usuario</th>
                                    <th>Cliente</th>
                                    <th>IP</th>
                                </tr>
                            </thead>
                            <tbody>
                                {audit.length === 0 ? (
                                    <tr><td colSpan="6" className="text-center text-muted py-3">Sin registros aún.</td></tr>
                                ) : audit.map((a) => (
                                    <tr key={a.id}>
                                        <td>{a.created_at ? new Date(a.created_at).toLocaleString() : '—'}</td>
                                        <td>{a.superadmin_username || '—'}</td>
                                        <td>
                                            <span className={`badge ${a.action === 'reveal' ? 'badge-danger' : 'badge-secondary'}`}>
                                                {a.action === 'reveal' ? 'Reveló contraseña' : 'Consultó listado'}
                                            </span>
                                        </td>
                                        <td>{a.target_nombre || a.target_email || '—'}</td>
                                        <td>{a.cliente_nombre || '—'}</td>
                                        <td>{a.ip_address || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <Modal show={showRevealModal} onHide={() => setShowRevealModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Revelar contraseña</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedUser && (
                        <p>
                            Usuario: <strong>{selectedUser.name} {selectedUser.lastname}</strong><br />
                            Email: <strong>{selectedUser.email}</strong>
                        </p>
                    )}
                    {!revealedPassword ? (
                        <p className="text-muted mb-0">Esta acción quedará registrada en auditoría. ¿Continuar?</p>
                    ) : (
                        <div className="alert alert-dark mb-0">
                            <strong>Contraseña:</strong>
                            <div className="mt-2 font-monospace">{revealedPassword}</div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRevealModal(false)}>Cerrar</Button>
                    {!revealedPassword && (
                        <Button variant="danger" onClick={handleRevealConfirm} disabled={revealing}>
                            {revealing ? 'Consultando…' : 'Revelar contraseña'}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </SuperadminLayout>
    );
};

export default ContraseñasUsuarios;
